import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Loader2, CheckCircle2, XCircle, FileText, User, Phone, MapPin, Bike,
  ShieldCheck, Eye, Clock, AlertCircle, Wallet,
} from "lucide-react";

const STATUS_TABS = [
  { key: "under_review", label: "Under Review", color: "amber" },
  { key: "approved", label: "Approved", color: "emerald" },
  { key: "rejected", label: "Rejected", color: "red" },
  { key: "kyc_pending", label: "KYC Pending", color: "slate" },
  { key: "training_pending", label: "Training Pending", color: "blue" },
  { key: "deposit_pending", label: "Deposit Pending", color: "violet" },
];

export default function AdminAgentApplications() {
  const [tab, setTab] = useState("under_review");
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchApps = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/agent-onboarding/admin/applications?status=${tab}`);
      setApps(data.applications || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchApps(); }, [tab]);

  const viewDetail = async (id) => {
    try {
      const { data } = await api.get(`/agent-onboarding/admin/applications/${id}`);
      setDetail(data);
    } catch (err) { toast.error("Failed to load detail"); }
  };

  const approve = async (id) => {
    setActionLoading(true);
    try {
      await api.post(`/agent-onboarding/admin/applications/${id}/approve`);
      toast.success("Agent approved");
      setDetail(null);
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally { setActionLoading(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error("Reason required"); return; }
    setActionLoading(true);
    try {
      await api.post(`/agent-onboarding/admin/applications/${rejectFor}/reject`, { reason: rejectReason });
      toast.success("Application rejected");
      setRejectFor(null); setRejectReason(""); setDetail(null);
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally { setActionLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-agent-applications">
      <div>
        <h1 className="font-['Outfit'] text-2xl font-bold text-slate-900">Agent Applications</h1>
        <p className="text-sm text-slate-500 mt-0.5">Review KYC + approve or reject gig partner applications</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${tab === t.key ? "bg-[#FF6B00] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#FF6B00]/30"}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? <div className="p-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
          : apps.length === 0 ? <div className="p-10 text-center text-sm text-slate-400">No applications in this status</div>
          : (
            <div className="divide-y divide-slate-100">
              {apps.map((a) => (
                <div key={a.id} className="p-5 hover:bg-slate-50/50 transition" data-testid={`app-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-['Outfit'] font-bold text-slate-900">{a.name}</span>
                        <span className="text-xs text-slate-500">{a.email}</span>
                        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600">{a.status}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${a.security_deposit_status === "paid" ? "bg-emerald-100 text-emerald-700" : a.security_deposit_status === "refunded" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                          <Wallet className="h-2.5 w-2.5 inline mr-0.5" />{a.security_deposit_status || "pending"}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.city}</span>
                        <span className="flex items-center gap-1"><Bike className="h-3 w-3" /> {a.vehicle_type}</span>
                        {a.applied_at && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(a.applied_at).toLocaleString()}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => viewDetail(a.id)} className="rounded-lg" data-testid={`view-detail-${a.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Review</Button>
                      {tab === "under_review" && <>
                        <Button size="sm" onClick={() => approve(a.id)} disabled={actionLoading} className="rounded-lg bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${a.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => { setRejectFor(a.id); setRejectReason(""); }} className="rounded-lg border-red-200 text-red-600 hover:bg-red-50" data-testid={`reject-${a.id}`}><XCircle className="h-3.5 w-3.5 mr-1" /> Reject</Button>
                      </>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-5 flex items-center justify-between">
              <div>
                <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">{detail.name}</h3>
                <p className="text-xs text-slate-500">{detail.email} · {detail.phone}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDetail(null)} data-testid="detail-close">✕</Button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Info label="City" value={detail.city} />
                <Info label="Vehicle" value={detail.vehicle_type} />
                <Info label="Aadhaar" value={detail.kyc_summary?.aadhaar_number} />
                <Info label="DL Number" value={detail.kyc_summary?.dl_number} />
                <Info label="Vehicle Number" value={detail.kyc_summary?.vehicle_number} />
                <Info label="Bank A/C" value={detail.kyc_summary?.bank_account_number} />
                <Info label="IFSC" value={detail.kyc_summary?.bank_ifsc} />
                <Info label="Status" value={detail.status} />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Documents</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    ["aadhaar_doc", "Aadhaar"],
                    ["dl_doc", "Driving Licence"],
                    ["rc_doc", "Vehicle RC"],
                    ["profile_photo", "Profile"],
                  ].map(([k, label]) => {
                    const v = detail.kyc_docs_full?.[k];
                    const isImg = v && typeof v === "string" && v.startsWith("data:image");
                    return (
                      <div key={k} className="border border-slate-200 rounded-xl p-2 text-center" data-testid={`doc-preview-${k}`}>
                        {isImg ? <a href={v} target="_blank" rel="noreferrer"><img src={v} alt={label} className="h-28 w-full rounded-lg object-cover" /></a>
                          : v ? <a href={v} target="_blank" rel="noreferrer" className="inline-flex flex-col items-center gap-1 py-5"><FileText className="h-6 w-6 text-[#FF6B00]" /><span className="text-[10px] text-slate-600">Open file</span></a>
                          : <div className="h-28 flex items-center justify-center text-xs text-slate-400">Missing</div>}
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-2">{label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {detail.status === "under_review" && (
                <div className="flex gap-2 pt-4 border-t border-slate-100">
                  <Button onClick={() => approve(detail.id)} disabled={actionLoading} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-xl h-11" data-testid="detail-approve-btn">
                    {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</>}
                  </Button>
                  <Button variant="outline" onClick={() => setRejectFor(detail.id)} className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-xl h-11" data-testid="detail-reject-btn">
                    <XCircle className="h-4 w-4 mr-1.5" /> Reject
                  </Button>
                </div>
              )}

              {detail.rejection_reason && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-100 flex gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-red-900">Rejection reason</p>
                    <p className="text-xs text-red-700 mt-0.5">{detail.rejection_reason}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject dialog */}
      {rejectFor && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => !actionLoading && setRejectFor(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="reject-dialog">
            <h3 className="font-['Outfit'] text-lg font-bold text-slate-900 mb-1">Reject Application</h3>
            <p className="text-sm text-slate-500 mb-4">The applicant will see this reason.</p>
            <Label>Reason</Label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-200 p-3 text-sm h-24 resize-none focus:border-[#FF6B00] focus:outline-none"
              placeholder="e.g., Aadhaar document is unclear"
              data-testid="reject-reason-input"
            />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setRejectFor(null)} className="flex-1 rounded-xl">Cancel</Button>
              <Button onClick={reject} disabled={actionLoading} className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl" data-testid="confirm-reject-btn">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Reject"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</p>
      <p className="text-sm font-semibold text-slate-900">{value || "—"}</p>
    </div>
  );
}
